import React from "react";
import clsx from "clsx";

import { ReactComponent as IconUser } from "../../../assets/images/AnswerList/icon_user.svg";
import { ReactComponent as IconPencil } from "../../../assets/images/AnswerList/icon_pencil.svg";
import { ReactComponent as IconTrash } from "../../../assets/images/AnswerList/icon_trash.svg";
import { IStudent, IAnswerComment } from "../../../state";
import ModalDeleteConfirm from "../../../molecules/ModalDeleteConfirm";
import { getConfirmModalMessage } from "../../../libs/domUtils";

import style from "./FormReply.module.scss";

type Props = {
  comment: IAnswerComment;
  currentUser?: IStudent;
  answerId: number;
  name_opened: boolean;
  calling?: boolean;
  onDeleteReply: (answerId: number, commentId: number) => void;
  onSendComment: (text: string, answerId: number, commentId?: number) => void;
};

const FormReply: React.FC<Props> = ({
  comment,
  onDeleteReply,
  onSendComment,
  currentUser,
  answerId,
  name_opened,
  calling,
}) => {
  const [showForm, setShowForm] = React.useState<boolean>(false);
  const [content, setContent] = React.useState<string>(comment.content);
  const [openModal, setOpenModal] = React.useState<{
    state: boolean;
    type: "delete" | "edit";
    answerId: number;
    commentId: number;
  }>({
    state: false,
    type: "delete",
    answerId: 0,
    commentId: 0,
  });

  const updateReply = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSendComment(content, comment.student!.id, comment.id);
    setShowForm(!showForm);
  };

  const modalMessage = getConfirmModalMessage({
    type: openModal.type,
  });

  const handleModalConfirm = () => {
    if (openModal.type === "delete") {
      onDeleteReply(openModal.answerId, openModal.commentId);
    }

    if (openModal.type === "edit") {
      setShowForm(false);
      setContent(comment.content);
    }

    setOpenModal({
      state: false,
      type: "delete",
      answerId: 0,
      commentId: 0,
    });
  };

  const isCurrentUser = currentUser && currentUser.id === comment.student?.id;

  return (
    <div className={style.body}>
      <IconUser
        className={clsx(style.userIcon, isCurrentUser && style.isCurrentUser)}
      />
      <div className={style.wrapper}>
        {name_opened ? (
          <div className={style.header}>
            {comment.student ? (
              <p
                className={clsx(
                  style.name,
                  isCurrentUser && style.isCurrentUser,
                )}
              >
                {`${comment.student.last_name} ${comment.student.first_name}`}
                {isCurrentUser && " (あなたのコメント)"}
              </p>
            ) : (
              <p className={style.name}>
                {`${comment.teacher?.last_name} ${comment.teacher?.first_name} 先生`}
              </p>
            )}
          </div>
        ) : isCurrentUser ? (
          <div className={style.header}>
            <p className={clsx(style.name, style.isCurrentUser)}>
              (あなたのコメント)
            </p>
          </div>
        ) : null}
        <div className={style.content}>
          {showForm ? (
            <form onSubmit={updateReply} className={style.form}>
              <textarea
                className={style.textarea}
                rows={3}
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="コメントを入力してください。"
              />
              <div className={style.buttonGroup}>
                <button
                  type="button"
                  className={style.cancelButton}
                  onClick={() => {
                    if (content !== comment.content) {
                      setOpenModal({
                        state: true,
                        type: "edit",
                        answerId,
                        commentId: comment.id,
                      });
                    } else {
                      setShowForm(false);
                    }
                  }}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  className={style.saveButton}
                  disabled={calling || !content || content === comment.content}
                >
                  保存する
                </button>
              </div>
            </form>
          ) : (
            <div className={style.comment}>
              <p className={style.text}>
                {comment.hidden_by === null
                  ? comment.content
                  : `${comment.hidden_by.last_name} ${comment.hidden_by.first_name}先生が、非表示にしました。`}
              </p>
              {isCurrentUser && (
                <div className={style.actions}>
                  <IconPencil
                    onClick={() => setShowForm(!showForm)}
                    className={clsx(calling && style.disabled)}
                  />
                  <IconTrash
                    className={clsx(calling && style.disabled)}
                    onClick={() => {
                      setOpenModal({
                        state: true,
                        type: "delete",
                        answerId,
                        commentId: comment.id,
                      });
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {openModal.state && (
        <ModalDeleteConfirm
          title={modalMessage}
          onConfirm={() => handleModalConfirm()}
          onClose={() =>
            setOpenModal({
              state: false,
              type: "delete",
              answerId: 0,
              commentId: 0,
            })
          }
        />
      )}
    </div>
  );
};

export default FormReply;
